import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useOperator } from '@/lib/auth';

/**
 * Admin Messages — IMAGE Corp / operator surface for editing the email
 * + SMS template library. Renders rows from `message_templates`, lets
 * operators edit subject + body inline, runs a "Send Test" flow against
 * the `send_test_draft` RPC (HUB-028: tests the draft, never saves it).
 *
 * RLS gates this page server-side: any UPDATE attempt by a non-operator
 * gets blocked by `operator update message_templates` policy. The
 * client-side gate is purely UX so the route doesn't render for Pros.
 *
 * Phase A scope:
 *   • List + filter + search
 *   • Edit subject + body_html (raw HTML; rich text editor later)
 *   • Live preview iframe with sample variables substituted
 *   • Send Test to current operator's email
 *
 * Deferred to a follow-up:
 *   • Variable picker that auto-injects {{tenant.first_name}} etc.
 *   • Version history / rollback
 *   • Create new template (today: importer-driven)
 *   • Multi-location overlay (when more locations come online)
 */

interface Template {
  id: string;
  location_id: string;
  workflow_name: string;
  channel: 'email' | 'sms';
  subject: string;
  body_html: string;
  variables: string[];
  version: number;
  notes: string | null;
  is_active: boolean;
  updated_at: string;
}

const SAMPLE_VARS: Record<string, string> = {
  'tenant.first_name': 'Eileen',
  'tenant.last_name': 'Burns DeJoie',
  'tenant.email': 'info@example.com',
  'tenant.suite_number': '108',
  'tenant.deposit_amount': '$2,400',
  'tenant.move_in_date': 'June 1, 2026',
  'location.display_name': 'IMAGE Studios Sharon',
  'owner.display_name': 'Peter Lewis',
  'owner.email': 'peter.lewis@imagestudios.co',
  'system.unsubscribe_url': 'https://hub.pkntops.com/unsubscribe',
};

function renderWithVars(template: string, vars: Record<string, string>): string {
  return template.replace(
    /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g,
    (_, key) =>
      vars[key] ??
      `<mark style="background:#fff3a3;padding:0 4px">{{${key}}}</mark>`,
  );
}

export default function Messages() {
  const navigate = useNavigate();
  const { operator, loading: opLoading } = useOperator();
  const qc = useQueryClient();

  // Route guard. Show neutral splash while resolving, then 404 for non-operators.
  useEffect(() => {
    if (!opLoading && !operator) navigate('/', { replace: true });
  }, [opLoading, operator, navigate]);

  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState<'all' | 'email' | 'sms'>('all');
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: templates, isLoading } = useQuery<Template[]>({
    queryKey: ['message-templates', operator?.id],
    enabled: !!operator,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('message_templates')
        .select('id,location_id,workflow_name,channel,subject,body_html,variables,version,notes,is_active,updated_at')
        .order('workflow_name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Template[];
    },
  });

  const filtered = useMemo(() => {
    if (!templates) return [];
    return templates.filter((t) => {
      if (channelFilter !== 'all' && t.channel !== channelFilter) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return t.workflow_name.toLowerCase().includes(s) ||
             t.subject.toLowerCase().includes(s) ||
             (t.notes ?? '').toLowerCase().includes(s);
    });
  }, [templates, search, channelFilter]);

  const editing = templates?.find((t) => t.id === editingId) ?? null;

  if (opLoading) {
    return <div className="text-image-charcoal text-sm">Checking access…</div>;
  }
  if (!operator) return null; // navigated away

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-image-charcoal font-semibold">Admin</div>
            <h1 className="font-display text-2xl font-bold text-image-black">
              Message <span className="text-image-pink">Templates</span>
            </h1>
          </div>
          <Link to="/admin/sequences" className="text-xs text-image-pink hover:text-image-plum font-semibold uppercase tracking-wider">
            Sequences →
          </Link>
        </div>
        <p className="text-sm text-image-charcoal mt-1">
          Edit the email + SMS bodies that go out to Pros and leads. Changes
          take effect on the next send. Use <strong>Send Test</strong> to
          preview a real send to your inbox before saving.
        </p>
      </div>

      <div className="card space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search workflows, subjects, notes…"
            className="flex-1 border border-image-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-image-pink"
          />
          <div className="flex gap-1">
            {(['all', 'email', 'sms'] as const).map((c) => (
              <button
                key={c}
                onClick={() => setChannelFilter(c)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors ${
                  channelFilter === c
                    ? 'bg-image-pink text-white'
                    : 'bg-image-fog text-image-charcoal hover:text-image-black'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="text-image-charcoal text-sm py-6 text-center">Loading templates…</div>
        ) : filtered.length === 0 ? (
          <div className="text-image-charcoal text-sm py-6 text-center">No templates match.</div>
        ) : (
          <div className="divide-y divide-image-border/60">
            {filtered.map((t) => (
              <button
                key={t.id}
                onClick={() => setEditingId(t.id)}
                className="w-full text-left py-3 hover:bg-image-fog/40 transition-colors px-2 -mx-2 rounded-lg"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                        t.channel === 'email' ? 'bg-image-pink-soft/40 text-image-plum' : 'bg-blue-50 text-blue-700'
                      }`}>{t.channel}</span>
                      <code className="text-xs text-image-black font-semibold">{t.workflow_name}</code>
                      <span className="text-[10px] text-image-charcoal">v{t.version}</span>
                    </div>
                    <div className="text-sm text-image-black truncate">{t.subject}</div>
                    {t.notes && <div className="text-[11px] text-image-charcoal mt-0.5 truncate">{t.notes}</div>}
                  </div>
                  <span className="text-image-charcoal shrink-0 text-lg">›</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {templates && (
          <div className="text-[11px] text-image-charcoal text-center pt-2 border-t border-image-border/60">
            {filtered.length} of {templates.length} templates · {templates.filter(t => t.channel === 'email').length} email · {templates.filter(t => t.channel === 'sms').length} SMS
          </div>
        )}
      </div>

      {editing && (
        <EditDrawer
          template={editing}
          operatorEmail={operator.email}
          onClose={() => setEditingId(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['message-templates'] })}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Edit drawer
// ────────────────────────────────────────────────────────────────────
/**
 * HUB-059: which templates the Send Test door can test. send_test_draft (migration
 * 20261011234200_hub028_send_test_draft.sql) raises for any channel but email.
 */
export function canSendTest(channel: Template['channel']): boolean {
  return channel === 'email';
}

export const SMS_TEST_UNAVAILABLE =
  'Test texts aren’t available yet — only email templates can be sent as a test. The preview below shows exactly what the text will say.';

export function EditDrawer(props: {
  template: Template;
  operatorEmail: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { template, operatorEmail, onClose, onSaved } = props;
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body_html);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Keep state in sync if the user opens a different template
  useEffect(() => {
    setSubject(template.subject);
    setBody(template.body_html);
    setStatusMsg(null);
  }, [template.id]);

  const dirty = subject !== template.subject || body !== template.body_html;

  const renderedSubject = useMemo(() => renderWithVars(subject, SAMPLE_VARS), [subject]);
  const renderedBody = useMemo(() => renderWithVars(body, SAMPLE_VARS), [body]);

  async function handleSave() {
    if (!dirty) return;
    setSaving(true); setStatusMsg(null);
    try {
      const { error } = await supabase
        .from('message_templates')
        .update({ subject, body_html: body })
        .eq('id', template.id);
      if (error) throw error;
      setStatusMsg({ kind: 'ok', text: 'Saved. Version bumps automatically.' });
      onSaved();
    } catch (e) {
      setStatusMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  /**
   * HUB-028 (Tenean review 2026-10-06): Send Test NEVER publishes. It used to save the edited
   * subject/body to message_templates first — the very rows scheduled_message_render_now reads at
   * dispatch — so testing a draft shipped it to every real recipient, even when the test then
   * failed. send_test_draft takes the draft as arguments, leaves the template alone, and sends only
   * to the signed-in person's own address. Only "Save changes" publishes.
   */
  const sendingRef = useRef(false);
  async function handleSendTest() {
    if (sendingRef.current) return; // a double press sends one test
    if (!canSendTest(template.channel)) return; // HUB-059: never call a door that can only refuse
    sendingRef.current = true;
    setSending(true); setStatusMsg(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('send_test_draft', {
        p_template_id: template.id,
        p_subject: subject,
        p_body_html: body,
        p_sample_vars: {},
      });
      if (rpcErr) throw rpcErr;
      const to = (data as { to?: string } | null)?.to ?? operatorEmail;
      setStatusMsg({
        kind: 'ok',
        text: dirty
          ? `Test of your unsaved draft queued to ${to}. Should arrive within ~60 seconds. The live template has NOT changed — press Save changes to publish it.`
          : `Test queued to ${to}. Should arrive within ~60 seconds.`,
      });
    } catch (e) {
      setStatusMsg({ kind: 'err', text: `Test not sent: ${(e as Error).message}. Nothing was published.` });
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-stretch justify-end" onClick={onClose}>
      <div
        className="bg-white w-full max-w-3xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Edit template"
      >
        <div className="sticky top-0 bg-white border-b border-image-border px-5 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                template.channel === 'email' ? 'bg-image-pink-soft/40 text-image-plum' : 'bg-blue-50 text-blue-700'
              }`}>{template.channel}</span>
              <code className="text-xs text-image-black font-semibold truncate">{template.workflow_name}</code>
              <span className="text-[10px] text-image-charcoal">v{template.version}</span>
            </div>
            {template.notes && <div className="text-[11px] text-image-charcoal mt-0.5 line-clamp-1">{template.notes}</div>}
          </div>
          <button onClick={onClose} className="text-image-charcoal hover:text-image-black ml-3 text-2xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="p-5 space-y-5">
          {statusMsg && (
            <div className={`text-sm rounded-lg p-3 ${
              statusMsg.kind === 'ok' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                       : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {statusMsg.text}
            </div>
          )}

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-image-charcoal font-semibold mb-1">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border border-image-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-image-pink"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-image-charcoal font-semibold mb-1">
              {template.channel === 'sms' ? 'SMS body' : 'HTML body'}
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={template.channel === 'sms' ? 6 : 18}
              className="w-full border border-image-border rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-image-pink"
              spellCheck={false}
            />
            {template.variables.length > 0 && (
              <div className="text-[11px] text-image-charcoal mt-2">
                Available variables: {template.variables.map((v) => (
                  <code key={v} className="bg-image-fog px-1.5 py-0.5 rounded mr-1 text-[10px]">{`{{${v}}}`}</code>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="btn-primary !bg-image-pink hover:!bg-image-plum flex-1 disabled:!bg-image-light-gray disabled:!text-image-charcoal"
            >
              {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
            {/* HUB-059 (Tenean review, fix-m4): send_test_draft sends EMAIL only — it refuses an SMS
                template ("only supports email channel today"). An SMS template is not offered a
                button that can only fail; it is told why, and the preview below is the test. */}
            {canSendTest(template.channel) ? (
              <button
                onClick={handleSendTest}
                disabled={sending}
                className="btn-ghost flex-1 disabled:opacity-50"
              >
                {sending ? 'Sending…' : dirty ? 'Send test of this draft to my inbox' : 'Send test to my inbox'}
              </button>
            ) : (
              <p className="flex-1 text-xs text-image-charcoal self-center" role="note">
                {SMS_TEST_UNAVAILABLE}
              </p>
            )}
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-image-charcoal font-semibold mb-2">
              Live preview · variables substituted with sample values
            </div>
            <div className="bg-image-fog rounded-lg p-3">
              <div className="text-xs text-image-charcoal mb-1">Subject</div>
              <div className="text-sm font-semibold text-image-black mb-3">{renderedSubject}</div>
              <div className="text-xs text-image-charcoal mb-1">Body</div>
              {template.channel === 'sms' ? (
                <pre className="bg-white border border-image-border rounded-lg p-4 text-sm whitespace-pre-wrap font-mono text-image-black">
                  {renderedBody.replace(/<[^>]+>/g, '').trim()}
                </pre>
              ) : (
                <iframe
                  title="preview"
                  className="w-full bg-white rounded-lg border border-image-border"
                  style={{ height: 600 }}
                  srcDoc={renderedBody}
                  sandbox=""
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
